import React from "react";
import { Card, Tag, Dropdown, Button, Tooltip } from "antd";
import {
  CloudServerOutlined,
  MoreOutlined,
  UserOutlined,
  CalendarOutlined,
  HeartOutlined,
  SwapOutlined,
  ReloadOutlined,
  DeleteOutlined,
  EditOutlined,
  SettingOutlined,
  DisconnectOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import CommonCardView from "../../../../global/cardView";
import { formatDateTime } from "../../../../../utils/functions";

const STATUS_COLORS = {
  unassigned: "default",
  active: "green",
  flagged: "orange",
  suspended: "red",
  retired: "red",
};

const isUnhealthy = (record) =>
  record.status === "flagged" || record.lastHealthCheckResult === "fail";

const StaticResidentialIpCard = ({
  record,
  onCardClick,
  onEdit,
  onAssignClick,
  onUnassign,
  onReassign,
  onHealthCheck,
  healthCheckingId,
  onStatus,
  onSuspend,
  onReplaceIp,
  onReactivate,
  replacingId,
  actionLoading,
}) => {
  const isAssigned = !!record.assignedUserEmail;
  const isHealthChecking = healthCheckingId === record._id;
  const isReplacing = replacingId === record._id;
  const unhealthy = isUnhealthy(record);
  const isSuspended = record.status === "suspended" || record.status === "retired";

  const moreItems = [
    {
      key: "edit",
      label: "Edit",
      icon: <EditOutlined />,
      onClick: () => onEdit?.(record),
    },
    ...(isAssigned
      ? [
          {
            key: "reassign",
            label: "Reassign from pool",
            icon: <ReloadOutlined />,
            onClick: () => onReassign?.(record),
          },
        ]
      : []),
    {
      key: "status",
      label: "Change Status",
      icon: <SettingOutlined />,
      onClick: () => onStatus?.(record),
    },
    ...(isSuspended
      ? [
          {
            key: "reactivate",
            label: "Reactivate",
            icon: <UndoOutlined />,
            onClick: () => onReactivate?.(record),
          },
        ]
      : []),
    { type: "divider" },
    {
      key: "suspend",
      label: "Suspend",
      icon: <DeleteOutlined />,
      danger: true,
      disabled: isSuspended,
      onClick: () => onSuspend?.(record),
    },
  ];

  return (
    <Card
      hoverable
      className="h-full relative !overflow-hidden cursor-pointer"
      onClick={() => onCardClick?.(record)}
    >
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center space-x-3 overflow-hidden min-w-0">
            <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <CloudServerOutlined className="text-primary text-lg" />
            </div>
            <div className="min-w-0 flex-1">
              <h3
                className="font-semibold text-base mb-0.5 truncate"
                title={`${record.host}:${record.port}`}
              >
                {record.host}:{record.port}
              </h3>
              <p className="text-gray-500 text-xs mb-0 capitalize">
                {record.type || "http"} · {record.provider || "webshare"}
              </p>
            </div>
          </div>
          <Tag
            color={STATUS_COLORS[record.status] || "default"}
            className="capitalize m-0 flex-shrink-0"
          >
            {record.status === "retired" ? "suspended" : record.status || "-"}
          </Tag>
        </div>

        <div className="space-y-2.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-gray-500 font-medium flex items-center gap-1">
              <UserOutlined />
              Assigned
            </span>
            <span
              className="text-sm text-gray-800 truncate max-w-[150px]"
              title={record.assignedUserEmail || ""}
            >
              {record.assignedUserEmail || "-"}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 font-medium">Lead Source</span>
            <span className="text-sm text-gray-800">{record.leadSource || "-"}</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 font-medium flex items-center gap-1">
              <HeartOutlined />
              Health
            </span>
            {record.lastHealthCheckResult ? (
              <Tag
                color={record.lastHealthCheckResult === "pass" ? "green" : "red"}
                className="uppercase m-0"
              >
                {record.lastHealthCheckResult}
              </Tag>
            ) : (
              <span className="text-sm text-gray-800">-</span>
            )}
          </div>

          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 font-medium flex items-center gap-1">
              <CalendarOutlined />
              Last Used
            </span>
            <span className="text-sm text-gray-800">
              {record.lastUsedAt ? formatDateTime(record.lastUsedAt) : "-"}
            </span>
          </div>
        </div>

        <div
          className="flex items-center gap-2 pt-3 border-t border-gray-100"
          onClick={(e) => e.stopPropagation()}
        >
          {isSuspended ? (
            <Tooltip title="Return this IP to the pool">
              <Button
                className="h-9 flex-1 !border-primary !text-primary hover:!bg-primary hover:!text-white text-xs font-medium"
                loading={actionLoading}
                onClick={() => onReactivate?.(record)}
              >
                <UndoOutlined />
                Reactivate
              </Button>
            </Tooltip>
          ) : unhealthy ? (
            <Tooltip title="Replace via Webshare (keeps assignment)">
              <Button
                type="primary"
                className="h-9 flex-1 text-xs font-medium flex items-center justify-center gap-1.5"
                loading={isReplacing}
                onClick={() => onReplaceIp?.(record)}
              >
                {!isReplacing && <ReloadOutlined />}
                Replace IP
              </Button>
            </Tooltip>
          ) : (
            <Tooltip title="Run health check">
              <Button
                className="h-9 flex-1 !border-primary !text-primary hover:!bg-primary hover:!text-white text-xs font-medium flex items-center justify-center gap-1.5"
                onClick={() => onHealthCheck?.(record)}
                loading={isHealthChecking}
                disabled={isHealthChecking}
              >
                {!isHealthChecking && <HeartOutlined />}
                Health
              </Button>
            </Tooltip>
          )}

          {!isAssigned && !isSuspended ? (
            <Tooltip title="Assign this IP to a user">
              <Button
                className="h-9 flex-1 !border-primary !text-primary hover:!bg-primary hover:!text-white text-xs font-medium flex items-center justify-center gap-1.5"
                onClick={() => onAssignClick?.(record)}
              >
                <SwapOutlined />
                Assign
              </Button>
            </Tooltip>
          ) : isAssigned && !isSuspended ? (
            <Tooltip title="Free this IP and return it to the pool">
              <Button
                className="h-9 flex-1 !border-primary !text-primary hover:!bg-primary hover:!text-white text-xs font-medium flex items-center justify-center gap-1.5"
                onClick={() => onUnassign?.(record)}
              >
                <DisconnectOutlined />
                Unassign
              </Button>
            </Tooltip>
          ) : null}

          <Dropdown menu={{ items: moreItems }} trigger={["click"]} placement="bottomRight">
            <Tooltip title="More actions">
              <Button
                className="h-9 !w-9 !min-w-9 !px-0 !border-primary !text-primary hover:!bg-primary hover:!text-white flex items-center justify-center"
                onClick={(e) => e.stopPropagation()}
                icon={<MoreOutlined />}
              />
            </Tooltip>
          </Dropdown>
        </div>
      </div>
    </Card>
  );
};

const EnhancedStaticResidentialIpsGridView = ({
  data,
  onCardClick,
  onEdit,
  onAssignClick,
  onUnassign,
  onReassign,
  onHealthCheck,
  healthCheckingId,
  onStatus,
  onSuspend,
  onReplaceIp,
  onReactivate,
  replacingId,
  actionLoading,
}) => {
  return (
    <CommonCardView>
      {data.map((record) => (
        <StaticResidentialIpCard
          key={record.key}
          record={record}
          onCardClick={onCardClick}
          onEdit={onEdit}
          onAssignClick={onAssignClick}
          onUnassign={onUnassign}
          onReassign={onReassign}
          onHealthCheck={onHealthCheck}
          healthCheckingId={healthCheckingId}
          onStatus={onStatus}
          onSuspend={onSuspend}
          onReplaceIp={onReplaceIp}
          onReactivate={onReactivate}
          replacingId={replacingId}
          actionLoading={actionLoading}
        />
      ))}
    </CommonCardView>
  );
};

export default EnhancedStaticResidentialIpsGridView;
